import axios from "axios";
import { toast } from "react-toastify";
import { cachedGet } from "../utils/apiCache";

// Resolve API base URL from env with sensible fallbacks
// Resolve API base URL from env with sensible fallbacks
const PRIMARY_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
// Secondary fallback: if primary host is down (e.g., AWS outage), retry against Vercel backend
const FALLBACK_BASE_URL =
  process.env.REACT_APP_API_FALLBACK_URL ||
  (PRIMARY_BASE_URL.includes("localhost")
    ? "http://localhost:5001"
    : "https://crezar-backend.vercel.app");

const api = axios.create({
  baseURL: PRIMARY_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Helper function to determine if current route is admin
const isAdminRoute = () => {
  return window.location.pathname.startsWith('/admin');
};

// Helper function to get appropriate token based on request context
const getToken = (config) => {
  // Check if this is an admin API call
  const isAdminAPI = config?.url?.includes('/auth/admin') || config?.baseURL?.includes('/auth/admin');
  if (isAdminAPI) {
    return localStorage.getItem('adminToken');
  }
  return localStorage.getItem('token');
};

// Helper function to clear appropriate tokens
const clearTokens = (adminOnly = false) => {
  if (adminOnly) {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminRole');
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getToken(config);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    if (response.data?.status === "jwterror") {
      console.error('[API] JWT Error in response:', response.config.url, response.data);
      const isAdminAPI = response.config?.url?.includes('/auth/admin');
      if (!window.__suppressAuthChecks) {
        clearTokens(isAdminAPI);
        // Avoid noisy toasts on login pages
        const onAuthPage = window.location.pathname.includes('/login') || window.location.pathname.includes('/signup');
        if (!onAuthPage) {
          toast.error(response.data.error || 'Authentication required');
        }
        if (isAdminAPI) {
          window.location.href = '/admin/login';
        } else {
          window.location.href = '/login';
        }
      }
      return Promise.reject(new Error(response.data.error || 'jwterror'));
    }
    return response;
  },
  (error) => {
    console.error('[API] Request Failed:', error.config?.url, error.response?.status, error.response?.data);

    // Network/timeout/server errors: retry ONCE against fallback base URL
    const shouldRetryWithFallback = (
      (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) ||
      (error.response && [502, 503, 504].includes(error.response.status))
    );

    const alreadyRetried = error.config?.__retriedWithFallback === true;
    const usingPrimary = (error.config?.baseURL || PRIMARY_BASE_URL) === PRIMARY_BASE_URL;

    if (shouldRetryWithFallback && !alreadyRetried && usingPrimary && FALLBACK_BASE_URL) {
      try {
        const retryConfig = {
          ...error.config,
          baseURL: FALLBACK_BASE_URL,
          __retriedWithFallback: true,
          headers: {
            ...(error.config?.headers || {}),
            'X-API-Fallback': '1',
          },
        };

        console.warn('[API] Retrying request against fallback backend:', FALLBACK_BASE_URL, 'for URL:', retryConfig.url);
        return api.request(retryConfig);
      } catch (e) {
        console.error('[API] Fallback retry failed to schedule:', e);
      }
    }
    if (error.response?.status === 401) {
      const isAdminAPI = error.config?.url?.includes('/auth/admin');
      if (!window.__suppressAuthChecks) {
        clearTokens(isAdminAPI);
        const onAuthPage = window.location.pathname.includes('/login') || window.location.pathname.includes('/signup');
        if (!onAuthPage) {
          toast.error(error.response?.data?.error || 'Unauthorized');
        }
        if (isAdminAPI) {
          window.location.href = '/admin/login';
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export { api };

export const signup = async (obj) => {
  try {
    const response = await api.post(`/auth/user/addUsers`, obj);
    return response;
  } catch (error) {
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Unable to connect to the server. Please make sure the backend server is running.');
    }

    // If the error has a response from the server
    if (error.response && error.response.data) {
      throw new Error(error.response.data.error || 'Signup failed');
    }

    throw error;
  }
};

export const refreshToken = async (token) => {
  try {
    const response = await api.post('/auth/user/refresh-token', {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const login = async (credentials) => {
  try {
    // Ensure we're sending the correct field names that backend expects
    const payload = {
      emailId: credentials.emailId, // Make sure this matches backend expectation
      password: credentials.password
    };

    console.log('[Login] Sending request with payload:', payload);
    const response = await api.post(`/auth/user/login-user`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('[Login] Response received:', response.data);
    // Return raw axios response so callers can branch on
    // response.data.login, response.data.token, and response.data.requiresOTP
    return response;

  } catch (error) {
    console.error('[Login] API Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });

    // Enhance error with server response if available
    if (error.response?.data?.error) {
      error.message = error.response.data.error;
    }

    throw error;
  }
};

export const getUserProfile = async (config = {}) => {
  try {
    // Extract user email or username from headers if it's an object
    const incomingHeaders = config.headers || {};
    let userInfo = incomingHeaders["User-Info"] ?? incomingHeaders["user-info"];
    if (typeof userInfo === 'object' && userInfo !== null) {
      userInfo = userInfo.emailId || userInfo.username || userInfo;
    }

    const updatedHeaders = {
      headers: {
        ...incomingHeaders,
        "User-Info": userInfo,
        "user-info": userInfo,
      }
    };

    // Use cachedGet for user profile
    const response = await cachedGet(`/auth/user/showUserInfo`, updatedHeaders);
    if (response.data.status === "success") {
      return response;
    } else {
      throw new Error(response.data.error || "Failed to fetch user profile");
    }
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (body, config = {}) => {
  try {
    // Extract user email or username from headers if it's an object
    const incomingHeaders = config.headers || {};
    let userInfo = incomingHeaders["User-Info"] ?? incomingHeaders["user-info"];
    if (typeof userInfo === 'object' && userInfo !== null) {
      userInfo = userInfo.emailId || userInfo.username || userInfo;
    }

    const updatedHeaders = {
      headers: {
        ...incomingHeaders,
        "User-Info": userInfo,
        "user-info": userInfo,
      }
    };

    const response = await api.put(`/auth/user/editUser`, body, updatedHeaders);
    return response;
  } catch (error) {
    throw error;
  }
};

export const verifySignupOTP = async (obj) => {
  try {
    const response = await api.post(`/auth/user/verify-signup-otp`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

export const resendSignupOtp = async (obj) => {
  try {
    const response = await api.post(`/auth/user/resend-signup-otp`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

export const sendContactMessage = async (obj) => {
  try {
    const response = await api.post(`/auth/user/contactus`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

export const createCard = async (body, headers) => {
  try {
    const response = await api.post(`/auth/user/createCard`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteCard = async (cardId, headers) => {
  try {
    const response = await api.delete(`/auth/user/deleteCard/${cardId}`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllCards = async (headers) => {
  try {
    // Extract user email or username from headers if it's an object
    let userInfo = headers["User-Info"];
    if (typeof userInfo === 'object' && userInfo !== null) {
      userInfo = userInfo.emailId || userInfo.username || userInfo;
    }

    const updatedHeaders = {
      ...headers,
      "User-Info": userInfo
    };

    // Use cachedGet for cards
    const response = await cachedGet(`/auth/user/getCards`, updatedHeaders);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getStreamers = async (headers) => {
  try {
    // Use cachedGet for streamers
    const response = await cachedGet(`/auth/user/getStreamers`, {
      ...headers,
      headers: {
        ...(headers?.headers || {}),
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
      validateStatus: function (status) {
        return status >= 200 && status < 500; // Resolve only if the status code is less than 500
      }
    });

    // If the response is 401 Unauthorized, clear the appropriate token and redirect
    if (response.status === 401) {
      clearTokens(false); // Clear user tokens only
      window.dispatchEvent(new Event('storage'));
      throw new Error('Session expired. Please log in again.');
    }

    // If the response is not ok, throw an error with the response data
    if (response.status >= 400) {
      const error = new Error(response.data?.error || 'Failed to fetch streamers');
      error.response = response;
      throw error;
    }

    // If the response is successful but has an error status in the data
    if (response.data?.status === 'error') {
      const error = new Error(response.data.error || 'Failed to fetch streamers');
      error.response = response;
      throw error;
    }

    // If the response contains streamers array directly
    if (Array.isArray(response.data)) {
      return { data: { status: 'success', streamers: response.data } };
    }

    // If the response has the expected structure
    if (response.data?.streamers) {
      return response;
    }

    // If we get here, the response format is unexpected
    console.error('Unexpected API response format:', response);
    return { data: { status: 'success', streamers: [] } };
  } catch (error) {
    console.error('Error in getStreamers API call:', error);

    // If it's an axios error with response, forward it
    if (error.response) {
      throw error;
    }

    // If it's a network error or other error, wrap it in a consistent format
    const wrappedError = new Error(error.message || 'Network error occurred');
    wrappedError.isNetworkError = !error.response;
    throw wrappedError;
  }
};

export const getStreamerCards = async (streamerEmailId, headers) => {
  try {
    // Bypass cache to ensure fresh data
    const response = await api.get(`/auth/user/getStreamerCards/${streamerEmailId}`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getUserCards = async (headers) => {
  try {
    // Use cachedGet for user cards
    const response = await cachedGet(`/auth/user/getUserCards`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const createTransaction = async (body, headers) => {
  try {
    const response = await api.post(`/auth/user/createTransaction`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Creators - public info for shareable URLs
export const getCreatorInfo = async (username) => {
  try {
    // Use cachedGet for public creator info
    const response = await cachedGet(`/api/creators/${encodeURIComponent(username)}/info`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const cardUse = async (body, headers) => {
  try {
    const response = await api.post(`/auth/user/useCard`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const generatedStaticUrl = async (headers) => {
  try {
    const response = await api.get(`/auth/user/showStreamerURL`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Admin Authentication
export const adminLogin = async (obj) => {
  try {
    const response = await api.post(`/auth/admin/login`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

export const adminForgotPassword = async (obj) => {
  try {
    const response = await api.post(`/auth/admin/forgot-password`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

export const adminResetPassword = async (obj) => {
  try {
    const response = await api.post(`/auth/admin/reset-password`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

// Admin Profile
export const getAdminProfile = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/profile`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateAdminProfile = async (body, headers) => {
  try {
    const response = await api.put(`/auth/admin/profile`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Admin Settings
export const getAdminSettings = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/settings`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateAdminSettings = async (body, headers) => {
  try {
    const response = await api.put(`/auth/admin/settings`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// User Management
export const getAllUsers = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/users`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const addUser = async (body, headers) => {
  try {
    const response = await api.post(`/auth/admin/users`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (userId, body, headers = {}) => {
  try {
    // Get admin token for admin operations
    if (!headers.Authorization) {
      const token = localStorage.getItem('adminToken');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add user-info header if not present
    if (!headers['User-Info']) {
      headers['User-Info'] = userId;
    }

    const response = await api.put(`/auth/admin/users/${userId}`, body, { headers });
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteUser = async (userId, headers) => {
  try {
    const response = await api.delete(`/auth/admin/users/${userId}`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Streamer Management
export const getAllStreamers = async (headers) => {
  try {
    const response = await api.get(`/auth/streamers`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const addStreamer = async (body, headers) => {
  try {
    const response = await api.post(`/auth/admin/streamers`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateStreamer = async (streamerId, body, headers) => {
  try {
    const response = await api.put(`/auth/admin/streamers/${streamerId}`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteStreamer = async (streamerId, headers) => {
  try {
    const response = await api.delete(`/auth/admin/streamers/${streamerId}`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Card Management
export const getAllCardsAdmin = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/cards`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const addCardAdmin = async (body, headers) => {
  try {
    const response = await api.post(`/auth/admin/cards`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateCardAdmin = async (cardId, body, headers) => {
  try {
    const response = await api.put(`/auth/admin/cards/${cardId}`, body, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteCardAdmin = async (cardId, headers) => {
  try {
    const response = await api.delete(`/auth/admin/cards/${cardId}`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Dashboard Statistics
export const getDashboardStats = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/dashboard/stats`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getRecentActivity = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/dashboard/activity`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

// Public Settings
export const getPublicSettings = async () => {
  try {
    const response = await api.get(`/auth/user/site-settings`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const forgotPassword = async (data) => {
  try {
    const response = await api.post(`/auth/user/forgot-password`, data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const verifyOTPforForget = async (obj) => {
  try {
    const response = await api.post(`/auth/user/verify-reset-otp`, obj);
    return response;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (data) => {
  try {
    const response = await api.post(`/auth/user/reset-password`, data);
    return response;
  } catch (error) {
    throw error;
  }
};

// Trending Cards API - separate from user-created cards
export const getTrendingCards = async (headers) => {
  try {
    // For now, use the same endpoint but we'll create a separate one in backend
    // This will fetch admin-curated trending cards instead of user-created cards
    const response = await cachedGet(`/auth/user/getTrendingCards`, headers);
    return response;
  } catch (error) {
    // Fallback to regular cards if trending endpoint doesn't exist yet
    console.warn('Trending cards endpoint not available, falling back to regular cards');
    return await getAllCards(headers);
  }
};

export const getContactMessagesAdmin = async (headers) => {
  try {
    const response = await api.get(`/auth/admin/contact-queries`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteContactMessageAdmin = async (id, headers) => {
  try {
    const response = await api.delete(`/auth/admin/contact-queries/${id}`, headers);
    return response;
  } catch (error) {
    throw error;
  }
};